import type JSZip from "jszip";

/**
 * Downloads an environment support bundle and adds it to a zip file.
 * On failure, adds an error file to the zip instead of throwing.
 */
export async function downloadEnvironmentSupportBundle(
    zip: JSZip,
    supportBundleUrl: string,
    logsToken: string,
): Promise<void> {
    try {
        const supportBundleResponse = await fetch(supportBundleUrl, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${logsToken}`,
            },
        });

        if (!supportBundleResponse.ok) {
            const errorMessage = `Failed to download environment support bundle: ${supportBundleResponse.status} ${supportBundleResponse.statusText}\nTimestamp: ${new Date().toISOString()}`;
            zip.file("environment-support-bundle-error.txt", errorMessage);
            return;
        }

        const supportBundleBlob = await supportBundleResponse.blob();
        zip.file("environment-support-bundle.zip", supportBundleBlob);
    } catch (error) {
        const errorMessage = `Failed to download environment support bundle: ${String(error)}\nTimestamp: ${new Date().toISOString()}`;
        zip.file("environment-support-bundle-error.txt", errorMessage);
    }
}

/**
 * Triggers a browser download for a blob with the given filename.
 */
export function triggerBlobDownload(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
}

/**
 * Downloads an environment support bundle zip and triggers a browser save.
 * Optionally accepts a callback to add extra files to the zip before saving.
 */
export async function downloadAndSaveSupportBundle(options: {
    supportBundleUrl: string;
    logsToken: string;
    filename: string;
    addExtras?: (zip: JSZip) => Promise<void>;
}): Promise<void> {
    const JSZipModule = (await import("jszip")).default;
    const zip = new JSZipModule();

    await downloadEnvironmentSupportBundle(zip, options.supportBundleUrl, options.logsToken);

    if (options.addExtras) {
        await options.addExtras(zip);
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    triggerBlobDownload(zipBlob, options.filename);
}
